/**
 * @description
 * Real-time sync progress indicator for Platform Sync dialog.
 * Shows current phase, progress, and status messages.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 12, 2025
 * @see admin/src/frontend/components/PlatformSync/PlatformSyncDialog.tsx
 */

import React from 'react';
import { RefreshCw, Database, Users, CheckCircle } from 'lucide-react';
import type { SyncProgress } from './types';

interface SyncProgressIndicatorProps {
  progress: SyncProgress;
}

/**
 * Component for displaying sync progress
 */
export const SyncProgressIndicator: React.FC<SyncProgressIndicatorProps> = ({
  progress,
}) => {
  const getPhaseIcon = () => {
    switch (progress.phase) {
      case 'fetching':
        return <RefreshCw className="h-8 w-8 text-primary animate-spin" />;
      case 'syncing_clients':
        return <Database className="h-8 w-8 text-primary animate-pulse" />;
      case 'syncing_users':
        return <Users className="h-8 w-8 text-primary animate-pulse" />;
      case 'complete':
        return <CheckCircle className="h-8 w-8 text-green-600" />;
      default:
        return <RefreshCw className="h-8 w-8 text-primary animate-spin" />;
    }
  };

  const getPhaseLabel = () => {
    switch (progress.phase) {
      case 'fetching':
        return 'Preparing...';
      case 'syncing_clients':
        return 'Syncing Clients';
      case 'syncing_users':
        return 'Syncing Users';
      case 'complete':
        return 'Complete';
      default:
        return 'Processing...';
    }
  };

  const progressPercentage =
    progress.total > 0 ? (progress.current / progress.total) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Icon and Phase */}
      <div className="flex flex-col items-center justify-center py-8">
        {getPhaseIcon()}
        <h3 className="text-xl font-semibold text-foreground mt-4">
          {getPhaseLabel()}
        </h3>
        <p className="text-sm text-muted-foreground mt-2">{progress.message}</p>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Progress</span>
          <span className="font-medium text-foreground">
            {progress.current} / {progress.total}
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
          <div
            className="bg-primary h-full transition-all duration-300 ease-out"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <div className="text-right text-sm text-muted-foreground">
          {Math.round(progressPercentage)}%
        </div>
      </div>

      {/* Phase Steps */}
      <div className="space-y-3">
        <div
          className={`flex items-center gap-3 p-3 rounded-lg ${
            progress.phase === 'fetching'
              ? 'bg-primary/10 border border-primary'
              : 'bg-green-50 border border-green-200'
          }`}
        >
          <div
            className={`h-6 w-6 rounded-full flex items-center justify-center ${
              progress.phase === 'fetching'
                ? 'bg-primary text-primary-foreground'
                : 'bg-green-600 text-white'
            }`}
          >
            {progress.phase !== 'fetching' ? '✓' : '1'}
          </div>
          <span className="font-medium text-foreground">Preparing sync</span>
        </div>

        <div
          className={`flex items-center gap-3 p-3 rounded-lg ${
            progress.phase === 'syncing_clients'
              ? 'bg-primary/10 border border-primary'
              : ['syncing_users', 'complete'].includes(progress.phase)
                ? 'bg-green-50 border border-green-200'
                : 'bg-muted border border-border'
          }`}
        >
          <div
            className={`h-6 w-6 rounded-full flex items-center justify-center ${
              progress.phase === 'syncing_clients'
                ? 'bg-primary text-primary-foreground'
                : ['syncing_users', 'complete'].includes(progress.phase)
                  ? 'bg-green-600 text-white'
                  : 'bg-muted-foreground text-white'
            }`}
          >
            {['syncing_users', 'complete'].includes(progress.phase) ? '✓' : '2'}
          </div>
          <span className="font-medium text-foreground">Syncing clients</span>
        </div>

        <div
          className={`flex items-center gap-3 p-3 rounded-lg ${
            progress.phase === 'syncing_users'
              ? 'bg-primary/10 border border-primary'
              : progress.phase === 'complete'
                ? 'bg-green-50 border border-green-200'
                : 'bg-muted border border-border'
          }`}
        >
          <div
            className={`h-6 w-6 rounded-full flex items-center justify-center ${
              progress.phase === 'syncing_users'
                ? 'bg-primary text-primary-foreground'
                : progress.phase === 'complete'
                  ? 'bg-green-600 text-white'
                  : 'bg-muted-foreground text-white'
            }`}
          >
            {progress.phase === 'complete' ? '✓' : '3'}
          </div>
          <span className="font-medium text-foreground">Syncing users</span>
        </div>
      </div>
    </div>
  );
};

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */